import { useState } from "react";
import { useAsync } from "../hooks/useAsync";
import { api } from "../api/client";
import { Card, ErrorMessage, PageHeading, Spinner } from "../components/ui";
import { useAuth } from "../hooks/useAuth";
import { isAdmin } from "../api/types";
import { Navigate } from "react-router-dom";

interface AdminStats {
  [key: string]: number | string;
}

interface AdminUser {
  id: string;
  email: string;
  displayName: string | null;
  role?: string;
  createdAt?: string;
}

const getStats = () => api.get<AdminStats>("/api/v1/admin-console/stats");
const getUsers = (limit: number, offset: number) =>
  api.get<{ users: AdminUser[]; total: number; limit: number; offset: number }>(
    `/api/v1/admin-console/users?limit=${limit}&offset=${offset}`,
  );

const PAGE_SIZE = 25;

export default function AdminPage() {
  const { user } = useAuth();
  const [offset, setOffset] = useState(0);
  const stats = useAsync(getStats, []);
  const users = useAsync(() => getUsers(PAGE_SIZE, offset), [offset]);

  // Defence in depth only — the API enforces this independently via
  // requireAdmin, so hiding the route is a UX nicety, not the control.
  if (user && !isAdmin(user)) return <Navigate to="/" replace />;

  const total = users.data?.total ?? 0;

  return (
    <div data-testid="admin-page">
      <PageHeading title="Admin" />

      <Card className="p-5">
        <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Stats</h2>
        {stats.loading && <Spinner />}
        {stats.error && <ErrorMessage error={stats.error} onRetry={stats.reload} />}
        {stats.data && (
          <dl className="mt-3 grid grid-cols-2 gap-4 sm:grid-cols-4" data-testid="admin-stats">
            {Object.entries(stats.data).map(([key, value]) => (
              <div key={key}>
                <dt className="text-xs capitalize text-gray-500 dark:text-gray-500">
                  {key.replace(/([A-Z])/g, " $1").toLowerCase()}
                </dt>
                <dd className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                  {String(value)}
                </dd>
              </div>
            ))}
          </dl>
        )}
      </Card>

      <Card className="mt-4 p-5">
        <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Users</h2>
        {users.loading && <Spinner />}
        {users.error && <ErrorMessage error={users.error} onRetry={users.reload} />}
        {users.data && (
          <>
            <ul className="mt-3 divide-y divide-gray-100 dark:divide-[#2e2e2e]">
              {users.data.users.map((u) => (
                <li
                  key={u.id}
                  className="flex items-center justify-between py-2.5 text-sm"
                  data-testid={`admin-user-${u.id}`}
                >
                  <span className="text-gray-900 dark:text-gray-100">
                    {u.displayName ?? u.email}
                  </span>
                  {u.role === "admin" && (
                    <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-xs text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-400">
                      admin
                    </span>
                  )}
                </li>
              ))}
            </ul>

            <div className="mt-4 flex items-center justify-between text-sm">
              <button
                onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
                disabled={offset === 0}
                className="font-medium text-emerald-700 disabled:opacity-40 dark:text-emerald-400"
                data-testid="admin-button-prev"
              >
                Previous
              </button>
              <span className="text-xs text-gray-500 dark:text-gray-500">
                {total === 0 ? 0 : offset + 1}–{Math.min(offset + PAGE_SIZE, total)} of {total}
              </span>
              <button
                onClick={() => setOffset(offset + PAGE_SIZE)}
                disabled={offset + PAGE_SIZE >= total}
                className="font-medium text-emerald-700 disabled:opacity-40 dark:text-emerald-400"
                data-testid="admin-button-next"
              >
                Next
              </button>
            </div>
          </>
        )}
      </Card>
    </div>
  );
}
